import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { useDeviceTier } from "../lib/useDeviceTier";

const PROJECTS = [
  {
    name: "Launch Labs",
    industry: "Creative Agency Concept",
    desc: "An original agency concept featuring custom 3D, cinematic motion and an editorial portfolio layout.",
    services: ["Creative Direction", "3D Experience", "Web Design"],
    video: "/assets/launch-labs-concept.mp4",
    image: "/assets/launch-labs-concept-poster.jpg",
  },
  {
    name: "NOIR & BLADE",
    industry: "Luxury Barber Concept",
    desc: "A dark, masculine barber experience built around cinematic identity, clear service pricing and direct appointment actions.",
    services: ["Brand Direction", "Motion Design", "Booking Journey"],
    video: "/assets/noir-blade-concept.mp4",
    image: "/assets/noir-blade-concept-poster.jpg",
  },
  {
    name: "Aurelia Hair Atelier",
    industry: "Luxury Salon Concept",
    desc: "An editorial salon concept combining refined service presentation, elegant motion and a curated visual portfolio.",
    services: ["Editorial Design", "Service Experience", "Portfolio Design"],
    video: "/assets/aurelia-hair-atelier-concept.mp4",
    image: "/assets/aurelia-hair-atelier-concept-poster.jpg",
  },
];

const PortfolioMedia = ({ project, videoEnabled }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (!project.video || !videoEnabled || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setShouldLoad(true);
      },
      { rootMargin: "240px 0px", threshold: 0.15 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [project.video, videoEnabled]);

  useEffect(() => {
    if (!videoRef.current || !shouldLoad) return;
    if (inView) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [inView, shouldLoad]);

  return (
    <div ref={containerRef} className="relative aspect-[800/393] overflow-hidden">
      {project.video && videoEnabled ? (
        <video
          ref={videoRef}
          src={shouldLoad ? project.video : undefined}
          poster={project.image}
          muted
          loop
          playsInline
          preload="none"
          aria-label={`${project.name} website concept preview`}
          className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
        />
      ) : (
        <img
          src={project.image}
          alt={`${project.name} website concept preview`}
          loading="lazy"
          className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
    </div>
  );
};

const BrowserFrameCard = ({ project, i, onOpen, videoEnabled }) => (
  <Reveal i={i % 3} className="h-full">
    <div
      data-testid={`project-card-${i}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl glass transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-white/25 hover:shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="ml-3 flex-1 truncate rounded-full bg-white/5 px-4 py-1 text-[11px] text-white/40 font-light">
          {project.name.toLowerCase().replace(/ /g, "")}.com
        </span>
      </div>
      <PortfolioMedia project={project} videoEnabled={videoEnabled} />
      <div className="flex flex-1 flex-col p-7">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-display text-[10px] tracking-[0.35em] text-white/40 uppercase">{project.industry}</p>
          <span className="rounded-full border border-white/15 px-3 py-1 font-display text-[9px] tracking-[0.2em] text-white/45 uppercase">Concept project</span>
        </div>
        <h3 className="font-display mt-2 text-2xl font-semibold tracking-tight text-white">{project.name}</h3>
        <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-white/50">{project.desc}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {project.services.map((s) => (
            <span key={s} className="rounded-full border border-white/15 px-3 py-1 text-[11px] text-white/55">{s}</span>
          ))}
        </div>
        <button
          data-testid={`project-view-button-${i}`}
          onClick={() => onOpen(project)}
          className="mt-6 inline-flex items-center gap-2 self-start text-sm font-medium text-white/70 transition-colors duration-300 hover:text-white"
        >
          View Project <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </div>
  </Reveal>
);

const Work = () => {
  const [active, setActive] = useState(null);
  const { tier, reducedMotion } = useDeviceTier();
  const videoEnabled = tier !== "low" && !reducedMotion;

  return (
    <section id="work" data-testid="work-section" className="relative z-10 py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <SectionHeading
          kicker="Selected Work"
          title="Selected Engagements."
          sub="Digital experiences designed to change how businesses are seen—and what customers do next."
          testId="work-heading"
        />
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {PROJECTS.map((p, i) => (
            <BrowserFrameCard key={p.name} project={p} i={i} onOpen={setActive} videoEnabled={videoEnabled} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md p-5"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 30 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-3xl overflow-hidden rounded-3xl glass"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-[800/393] bg-black">
                {active.video ? (
                  <video
                    src={active.video}
                    poster={active.image}
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <img src={active.image} alt={active.name} className="h-full w-full object-cover grayscale" />
                )}
                <button
                  data-testid="project-modal-close"
                  onClick={() => setActive(null)}
                  className="absolute right-4 top-4 rounded-full bg-black/60 p-2 text-white/80 hover:text-white backdrop-blur-md transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-display text-[10px] tracking-[0.35em] text-white/40 uppercase">{active.industry}</p>
                  <span className="rounded-full border border-white/15 px-3 py-1 font-display text-[9px] tracking-[0.2em] text-white/45 uppercase">Concept project</span>
                </div>
                <h3 className="font-display mt-2 text-3xl font-semibold tracking-tight">{active.name}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{active.desc}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Work;
